import { QuestionMarkCircleIcon } from '@heroicons/react/24/solid';
import { useQueryClient } from '@tanstack/react-query';
import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

import Button, { ButtonVariant } from 'components/Button/Button';
import Panel from 'components/Panel/Panel';
import Placeholder from 'components/Placeholder/Placeholder';
import useCreateQuestion from 'hooks/useCreateQuestion';
import {
  CompetencyDetailsResponse,
  Question,
  QuestionType,
} from 'hooks/useGetCompetencyDetails';
import useNotification from 'hooks/useNotification';
import { t } from 'locales/i18n';
import CompetencyQuestionsForm from 'pages/Competency/CompetencyQuestionsForm';
import QuestionCard from 'pages/Competency/QuestionCard';
import QuestionCardWithStatus from 'pages/Competency/QuestionCardWithStatus';

interface CompetencyQuestionsProps {
  questions: Question[];
  isLoading?: boolean;
}

interface IFileUploadData {
  name: string;
  isLoading?: boolean;
  errorMessage?: string;
}

const CompetencyQuestions = ({
  questions = [],
  isLoading: isLoadingProp,
}: CompetencyQuestionsProps) => {
  const inputFileRef = useRef<HTMLInputElement>(null);
  const { interviewUid = '', competencyUid = '' } = useParams();
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [error, setError] = useState('');
  const [uploadedFilesData, setUploadedFilesData] = useState<IFileUploadData[]>(
    []
  );
  const [successfulUploadIds, setSuccessfulUploadIds] = useState<string[]>([]);
  const [mutatedQuestionUid, setMutatedQuestionUid] = useState<string | null>(
    null
  );
  const { showErrorNotification, showSuccessNotification } = useNotification();
  const { createQuestion, isLoading } = useCreateQuestion(competencyUid);
  const queryClient = useQueryClient();

  const fileSizeLimit = 50; // 50MB

  useEffect(() => {
    setIsFormOpen(false);
    setUploadedFilesData([]);
  }, [competencyUid]);

  const resetFileInput = () => {
    if (inputFileRef.current) {
      inputFileRef.current.value = '';
    }
  };

  const handleDismissNotification = (index: number) => {
    setUploadedFilesData((prev) => prev.filter((_, i) => i !== index));
  };

  const handleUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      Array.from(event.target.files).forEach(({ name, size }) => {
        const fileSizeMegaBytes = size / (1024 * 1024);
        const errorMessage = t(
          'File exceeds maximum upload size (50MB). Please compress your file or try uploading a different file.'
        );
        setUploadedFilesData((prev) => [
          ...prev,
          {
            name,
            isLoading: false,
            errorMessage: fileSizeMegaBytes > fileSizeLimit ? errorMessage : '',
          },
        ]);
        if (fileSizeMegaBytes > fileSizeLimit) {
          setError(errorMessage);

          resetFileInput();
        }
      });
    }

    if (event.target?.files && event.target.files[0] instanceof Blob) {
      Promise.all(
        Array.from(event.target.files).map((file, index) => {
          setError('');
          return onSubmit(file, uploadedFilesData.length + index);
        })
      ).finally(() => {
        queryClient.invalidateQueries([
          'getCompetencyDetails',
          interviewUid,
          competencyUid,
        ]);
      });
    }
  };

  const onSubmit = async (video: Blob, index: number) => {
    setMutatedQuestionUid(null);
    setUploadedFilesData((prev) =>
      prev.reduce<IFileUploadData[]>(
        (acc, curr, i) =>
          i === index
            ? [...acc, { name: curr.name, isLoading: true }]
            : [...acc, curr],
        []
      )
    );
    createQuestion({
      preparationTime: 30,
      questionText: video.name,
      questionType: QuestionType.VIDEO,
      responseTime: 60,
      video,
    })
      .then(
        async ({
          question_text: questionText,
          preparation_time: preparationTime,
          question_type: questionType,
          response_time: responseTime,
          word_limit: wordLimit,
          video,
          question_uid: questionUid,
        }) => {
          resetFileInput();
          setSuccessfulUploadIds((prev) => [...prev, questionUid]);
          setUploadedFilesData((prev) =>
            prev.reduce<IFileUploadData[]>(
              (acc, curr, i) =>
                i === index
                  ? [...acc, { name: curr.name, isLoading: false }]
                  : [...acc, curr],
              []
            )
          );
          setMutatedQuestionUid(questionUid);

          showSuccessNotification(
            t('Question Added'),
            t('Your question was successfully added')
          );

          const oldData = queryClient.getQueryData<CompetencyDetailsResponse>([
            'getCompetencyDetails',
            interviewUid,
            competencyUid,
          ]);

          if (!oldData) {
            return;
          }

          queryClient.setQueryData(
            ['getCompetencyDetails', interviewUid, competencyUid],
            {
              ...oldData,
              questions: [
                ...(oldData?.questions || []),
                {
                  uid: questionUid,
                  question_text: questionText,
                  preparation_time: preparationTime,
                  question_type: questionType,
                  response_time: responseTime,
                  word_limit: wordLimit,
                  video,
                },
              ],
            }
          );
          setTimeout(() => {
            setSuccessfulUploadIds(
              successfulUploadIds.filter((uploadId) => uploadId === questionUid)
            );
          }, 60000);
        }
      )
      .catch((error) => {
        showErrorNotification(
          t('There was an error creating the question.'),
          error.message
        );

        setUploadedFilesData((prev) =>
          prev.reduce<IFileUploadData[]>(
            (acc, curr, i) =>
              i === index
                ? [
                    ...acc,
                    {
                      name: curr.name,
                      isLoading: false,
                      errorMessage: error.message,
                    },
                  ]
                : [...acc, curr],
            []
          )
        );
      });
  };

  const isUploadFilesEnabled = questions.every(
    (question) => question.question_type === QuestionType.VIDEO
  );

  return (
    <Panel
      panelClassName="space-y-2"
      title={t('Questions')}
      dataTestId="questionStepCard"
      isLoading={isLoadingProp}
      description={t(
        'Applicants will randomly receive one of the questions listed below.'
      )}
    >
      {!isFormOpen && questions.length === 0 && !isLoading && !error ? (
        <Placeholder
          icon={<QuestionMarkCircleIcon />}
          title={t('No questions added yet')}
          description={t(
            'Add questions manually or upload video files using the buttons below.'
          )}
        >
          <div className="flex justify-center gap-2">
            <Button
              variant={ButtonVariant.SECONDARY}
              onClick={() => setIsFormOpen(true)}
              buttonText={t('Add Question')}
              disabled={isFormOpen || isLoading}
              dataTestId="addQuestion"
            />
            <Button
              variant={ButtonVariant.SECONDARY}
              onClick={() => inputFileRef?.current?.click()}
              buttonText={t('Upload Files')}
              dataTestId="uploadFilesButton"
              disabled={isFormOpen || isLoading}
            />
          </div>
        </Placeholder>
      ) : (
        questions.map((question) => (
          <QuestionCard
            successfulUploadIds={successfulUploadIds}
            key={question.uid}
            question={question}
            showIconAnimation={question.uid === mutatedQuestionUid}
            onUpdateAndAddAnother={() => setIsFormOpen(true)}
          />
        ))
      )}

      {isFormOpen && (
        <CompetencyQuestionsForm closeForm={() => setIsFormOpen(false)} />
      )}

      {uploadedFilesData.map(
        ({ name, errorMessage, isLoading: isVideoUploading }, index) => (
          <>
            {isVideoUploading ? (
              <QuestionCardWithStatus
                key={name}
                isUploading={isVideoUploading}
                uploadedFileText={name}
              />
            ) : null}

            {errorMessage && (
              <QuestionCardWithStatus
                key={name}
                errorMessage={errorMessage}
                uploadedFileText={name}
                index={index}
                handleDismiss={handleDismissNotification}
              />
            )}
          </>
        )
      )}

      {questions.length > 0 || isLoading || error ? (
        <div className="!mt-4 flex gap-2">
          <Button
            variant={ButtonVariant.SECONDARY}
            onClick={() => setIsFormOpen(true)}
            buttonText={t('Add Question')}
            dataTestId="addQuestion"
            disabled={isFormOpen || isLoading}
          />
          {isUploadFilesEnabled && (
            <Button
              variant={ButtonVariant.SECONDARY}
              onClick={() => inputFileRef?.current?.click()}
              buttonText={t('Upload Files')}
              dataTestId="uploadFilesButton"
              disabled={isFormOpen || isLoading}
            />
          )}
        </div>
      ) : null}

      <input
        type="file"
        ref={inputFileRef}
        id="file-uploader-video"
        accept=".mp4"
        multiple
        name="file-uploader"
        className="hidden"
        data-testid="videoFileUploader"
        onChange={handleUpload}
        disabled={isLoading}
      />
    </Panel>
  );
};

export default CompetencyQuestions;
